import { useEffect, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Label } from "../../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { Textarea } from "../../../../../components/ui/textarea";
import { useToast } from "../../../../../hooks/use-toast";
import {
  mensagemErro,
  useBloqueioMutations,
  useOpcoesBloqueio,
} from "../hooks/use-bloqueios";
import { BloqueioResultado } from "../types";
import { formatDocumento, parseDocumentos } from "../utils/bloqueio-utils";

/**
 * - documento: bloqueia o CPF/CNPJ inteiro na ops do cadastro (vem da linha).
 * - telefone:  desativa só aquele cadastro (roteamento: a oferta cai em outro telefone).
 * - massa:     lista colada ou CSV, bloquear ou desbloquear numa ops escolhida.
 */
export type BloquearModo =
  | { tipo: "documento"; documento: string; ops: string }
  | { tipo: "telefone"; documento: string; ops: string; telefone: string }
  | { tipo: "massa" };

interface BloquearDialogProps {
  modo: BloquearModo | null;
  onClose: () => void;
  onDone?: () => void;
}

const SEM_MOTIVO = "__sem_motivo__";

export function BloquearDialog({ modo, onClose, onDone }: BloquearDialogProps) {
  const { toast } = useToast();
  const { data: opcoes } = useOpcoesBloqueio();
  const { bloquear, desbloquear } = useBloqueioMutations(onDone);

  const [acao, setAcao] = useState<"bloquear" | "desbloquear">("bloquear");
  const [texto, setTexto] = useState("");
  const [ops, setOps] = useState("");
  const [motivo, setMotivo] = useState(SEM_MOTIVO);
  const [motivoTexto, setMotivoTexto] = useState("");
  const [resultado, setResultado] = useState<BloqueioResultado | null>(null);

  useEffect(() => {
    setAcao("bloquear");
    setTexto("");
    setOps(modo && modo.tipo !== "massa" ? modo.ops : "");
    setMotivo(modo?.tipo === "telefone" ? "roteamento" : SEM_MOTIVO);
    setMotivoTexto("");
    setResultado(null);
  }, [modo]);

  if (!modo) return null;

  const massa = modo.tipo === "massa";
  const documentos = massa ? parseDocumentos(texto) : [modo.documento];
  const motivosDisponiveis = (opcoes?.motivos ?? []).filter(
    (m) => modo.tipo === "telefone" || !m.exigeTelefone,
  );
  const outro = motivo === "outro";
  const pendente = bloquear.isPending || desbloquear.isPending;
  const podeEnviar =
    documentos.length > 0 &&
    !!ops &&
    (acao === "desbloquear" || !outro || !!motivoTexto.trim()) &&
    !pendente;

  const lerArquivo = async (file?: File) => {
    if (!file) return;
    const conteudo = await file.text();
    setTexto((atual) => (atual ? `${atual}\n${conteudo}` : conteudo));
  };

  const enviar = async () => {
    try {
      const res =
        acao === "desbloquear"
          ? await desbloquear.mutateAsync({ documentos, ops_entregador: ops })
          : await bloquear.mutateAsync({
              documentos,
              ops_entregador: ops,
              telefone: modo.tipo === "telefone" ? modo.telefone : undefined,
              motivo_codigo: motivo === SEM_MOTIVO ? undefined : motivo,
              motivo_texto: outro ? motivoTexto.trim() : undefined,
            });

      if (massa) {
        setResultado(res);
        return;
      }
      if (res.success.length > 0) {
        toast({
          title:
            modo.tipo === "telefone"
              ? "Telefone desativado"
              : "Entregador bloqueado",
        });
        onClose();
      } else {
        toast({
          title: "Nada foi feito",
          description: res.failed[0]?.reason,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: mensagemErro(error),
        variant: "destructive",
      });
    }
  };

  const titulo =
    modo.tipo === "telefone"
      ? "Desativar telefone (roteamento)"
      : modo.tipo === "documento"
        ? "Bloquear CPF/CNPJ na operação"
        : "Bloqueio em massa";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {modo.tipo === "telefone"
              ? `A oferta deixa de ir para ${modo.telefone} e passa a usar outro cadastro de ${formatDocumento(modo.documento)} na ops ${modo.ops}.`
              : modo.tipo === "documento"
                ? `${formatDocumento(modo.documento)} deixa de receber oferta em todos os cadastros da ops ${modo.ops}.`
                : "Cole a lista de CPFs/CNPJs (um por linha, ou separados por vírgula) ou carregue um CSV."}
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <ResultadoMassa resultado={resultado} acao={acao} />
        ) : (
          <div className="space-y-4">
            {massa && (
              <>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={acao === "bloquear" ? "default" : "outline"}
                    onClick={() => setAcao("bloquear")}
                  >
                    Bloquear
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={acao === "desbloquear" ? "default" : "outline"}
                    onClick={() => setAcao("desbloquear")}
                  >
                    Desbloquear
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bloqueio-docs">CPFs/CNPJs</Label>
                  <Textarea
                    id="bloqueio-docs"
                    rows={6}
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder={"123.456.789-01\n12.345.678/0001-95"}
                  />
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{documentos.length} documento(s)</span>
                    <input
                      type="file"
                      accept=".csv,.txt"
                      aria-label="Carregar CSV"
                      onChange={(e) => lerArquivo(e.target.files?.[0])}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Operação</Label>
                  <Select value={ops} onValueChange={setOps}>
                    <SelectTrigger>
                      <SelectValue placeholder="Escolha a ops" />
                    </SelectTrigger>
                    <SelectContent>
                      {(opcoes?.ops ?? []).map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {acao === "bloquear" && (
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Select value={motivo} onValueChange={setMotivo}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_MOTIVO}>Sem motivo</SelectItem>
                    {motivosDisponiveis.map((m) => (
                      <SelectItem key={m.codigo} value={m.codigo}>
                        {m.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {outro && (
                  <Textarea
                    aria-label="Descreva o motivo"
                    rows={2}
                    value={motivoTexto}
                    onChange={(e) => setMotivoTexto(e.target.value)}
                    placeholder="Descreva o motivo"
                  />
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pendente}>
            {resultado ? "Fechar" : "Cancelar"}
          </Button>
          {!resultado && (
            <Button
              onClick={enviar}
              disabled={!podeEnviar}
              variant={acao === "bloquear" ? "destructive" : "default"}
            >
              {pendente
                ? "Enviando..."
                : acao === "bloquear"
                  ? "Confirmar bloqueio"
                  : "Confirmar desbloqueio"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultadoMassa({
  resultado,
  acao,
}: {
  resultado: BloqueioResultado;
  acao: "bloquear" | "desbloquear";
}) {
  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium text-green-700">
        {resultado.success.length}{" "}
        {acao === "bloquear" ? "bloqueado(s)" : "desbloqueado(s)"}
      </p>
      {resultado.failed.length > 0 && (
        <div>
          <p className="font-medium text-red-700">
            {resultado.failed.length} não processado(s)
          </p>
          <ul className="max-h-40 overflow-y-auto text-muted-foreground">
            {resultado.failed.map((f, i) => (
              <li key={i}>
                {f.documento ? formatDocumento(f.documento) : `#${f.id}`}:{" "}
                {f.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {resultado.avisos.length > 0 && (
        <div>
          <p className="font-medium text-amber-700">
            {resultado.avisos.length} aviso(s)
          </p>
          <ul className="max-h-40 overflow-y-auto text-muted-foreground">
            {resultado.avisos.map((a, i) => (
              <li key={i}>
                {formatDocumento(a.documento)}: {a.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
